const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const ifscRegex = /^[A-Z]{4}0[0-9]{6}$/;

export function parseBseMessages(body) {
  const messages = body?.messages || body?.data?.messages;
  if (Array.isArray(messages) && messages.length) {
    return messages
      .map((m) => m?.msg_text || m?.message || m?.msg || JSON.stringify(m))
      .filter(Boolean)
      .join(" ");
  }
  return body?.message || "";
}

export function isBseSuccess(body) {
  if (!body || typeof body !== "object") return false;
  if (String(body.status || "").toLowerCase() === "error") return false;
  const messages = body.messages || body?.data?.messages;
  if (Array.isArray(messages) && messages.length) return false;
  return true;
}

function pickPanIdentifier(holder) {
  const ids = Array.isArray(holder?.identifier)
    ? holder.identifier
    : holder?.identifier && typeof holder.identifier === "object"
      ? [holder.identifier]
      : [];
  return ids.find((id) => String(id?.identifier_type || "").toLowerCase() === "pan") || {};
}

export const emptyUccUpdateForm = {
  holding_nature: "SI",
  tax_code: "01",
  rdmp_idcw_pay_mode: "02",
  comm_mode: "E",
  first_name: "",
  middle_name: "",
  last_name: "",
  dob: "",
  gender: "M",
  pan: "",
  occ_code: "02",
  auth_mode: "M",
  kyc_type: "K",
  email: "",
  phone: "",
  country_code: "91",
  addr1: "",
  addr2: "",
  addr3: "",
  city: "",
  state: "",
  postalcode: "",
  country: "INDIA",
  ifsc_code: "",
  bank_acc_num: "",
  bank_acc_type: "SB",
  depository_code: "CDSL",
  dp_id: "",
  client_id: "",
  uccStatus: "",
  _originalBank: null,
  _originalDepository: null,
};

export function detailFromGetUcc(body) {
  const detail = body?.data && typeof body.data === "object" ? body.data : body;
  const holders = Array.isArray(detail?.holder) ? detail.holder : [];
  const holder = holders[0] || {};
  const person = holder.person && typeof holder.person === "object" ? holder.person : {};
  const contacts = Array.isArray(holder.contact) ? holder.contact : [];
  const prContact =
    contacts.find((c) => String(c.contact_type || "").toUpperCase() === "PR") ||
    contacts[0] ||
    {};
  const comm = detail.comm_addr && typeof detail.comm_addr === "object" ? detail.comm_addr : {};
  const panRow = pickPanIdentifier(holder);

  const banks = Array.isArray(detail.bank_account) ? detail.bank_account : [];
  const bank = banks[0] || {};
  const deps = Array.isArray(detail.depository) ? detail.depository : [];
  const dep = deps[0] || {};

  return {
    holding_nature: detail.holding_nature || "SI",
    tax_code: detail.tax_code || "01",
    rdmp_idcw_pay_mode: detail.rdmp_idcw_pay_mode || "02",
    comm_mode: detail.comm_mode || "E",
    first_name: person.first_name || "",
    middle_name: person.middle_name || "",
    last_name: person.last_name || "",
    dob: person.dob || "",
    gender: person.gender || "M",
    pan: (panRow.identifier_number || "").toUpperCase(),
    occ_code: holder.occ_code || "02",
    auth_mode: holder.auth_mode || "M",
    kyc_type: holder.kyc_type || "K",
    email: prContact.email_address || "",
    phone: prContact.contact_number || "",
    country_code: prContact.country_code || "91",
    addr1: comm.address_line_1 || "",
    addr2: comm.address_line_2 || "",
    addr3: comm.address_line_3 || "",
    city: comm.city || "",
    state: comm.state || "",
    postalcode: comm.postalcode || "",
    country: comm.country || "INDIA",
    ifsc_code: bank.ifsc_code || "",
    bank_acc_num: bank.bank_acc_num || "",
    bank_acc_type: bank.bank_acc_type || "SB",
    depository_code: dep.depository_code || "CDSL",
    dp_id: dep.dp_id || "",
    client_id: dep.client_id || "",
    uccStatus: detail.ucc_status || "",
    _originalBank: bank.ifsc_code || bank.bank_acc_num ? { ...bank } : null,
    _originalDepository: dep.dp_id || dep.client_id ? { ...dep } : null,
  };
}

export function validateUccUpdateForm(form) {
  if (!form.first_name.trim() || !form.last_name.trim()) {
    return "First and last name are required.";
  }
  if (!form.dob) return "Date of birth is required.";
  const pan = form.pan.trim().toUpperCase();
  if (!panRegex.test(pan)) return "Enter a valid PAN (e.g. ABCDE1234F).";
  const phone = String(form.phone || "").replace(/\D/g, "").slice(-10);
  if (phone.length !== 10) return "Mobile number must be 10 digits.";
  if (!form.email.trim()) return "Email is required.";
  if (!form.addr1.trim() || !form.city.trim() || !form.state.trim()) {
    return "Address line 1, city and state are required.";
  }
  const pin = String(form.postalcode || "").trim();
  if (!/^\d{6}$/.test(pin)) return "PIN code must be 6 digits.";
  const ifsc = form.ifsc_code.trim().toUpperCase();
  if (!ifscRegex.test(ifsc)) return "Enter a valid IFSC code.";
  if (!form.bank_acc_num.trim()) return "Bank account number is required.";
  if (!form.dp_id.trim() || !form.client_id.trim()) {
    return "DP ID and demat client ID are required.";
  }
  return null;
}

export function buildUpdateUccPayload(form, memberId, clientCode) {
  const mid = String(memberId || "63164").trim() || "63164";
  const code = String(clientCode || "").trim();
  const pin = String(form.postalcode || "").trim();
  const pan = form.pan.trim().toUpperCase();
  const phoneDigits = String(form.phone || "").replace(/\D/g, "").slice(-10);

  const data = {
    member: { member_id: mid },
    investor: { client_code: code },
    holding_nature: form.holding_nature,
    tax_code: form.tax_code,
    rdmp_idcw_pay_mode: form.rdmp_idcw_pay_mode,
    comm_mode: form.comm_mode,
    comm_addr: {
      address_line_1: form.addr1.trim(),
      ...(form.addr2.trim() ? { address_line_2: form.addr2.trim() } : {}),
      ...(form.addr3.trim() ? { address_line_3: form.addr3.trim() } : {}),
      city: form.city.trim(),
      state: form.state.trim(),
      postalcode: pin,
      country: (form.country || "INDIA").trim(),
    },
    holder: {
      add: [
        {
          holder_rank: "1",
          occ_code: form.occ_code,
          auth_mode: form.auth_mode,
          is_pan_exempt: false,
          kyc_type: form.kyc_type,
          person: {
            first_name: form.first_name.trim(),
            ...(form.middle_name.trim() ? { middle_name: form.middle_name.trim() } : {}),
            last_name: form.last_name.trim(),
            dob: form.dob,
            gender: form.gender,
          },
          identifier: {
            identifier_type: "pan",
            identifier_number: pan,
          },
          contact: [
            {
              contact_number: phoneDigits,
              country_code: String(form.country_code || "91").replace(/\D/g, "") || "91",
              whose_contact_number: "SE",
              email_address: form.email.trim(),
              whose_email_address: "SE",
              contact_type: "PR",
            },
          ],
        },
      ],
    },
  };

  const newBank = {
    ifsc_code: form.ifsc_code.trim().toUpperCase(),
    bank_acc_num: form.bank_acc_num.trim(),
    bank_acc_type: form.bank_acc_type,
    account_owner: "SELF",
  };
  const ob = form._originalBank;
  const bankChanged =
    !ob ||
    String(ob.ifsc_code || "").toUpperCase() !== newBank.ifsc_code ||
    String(ob.bank_acc_num || "").trim() !== newBank.bank_acc_num ||
    String(ob.bank_acc_type || "SB") !== newBank.bank_acc_type;

  if (bankChanged) {
    data.bank_account = { add: [newBank] };
    if (ob?.ifsc_code && ob?.bank_acc_num) {
      data.bank_account.delete = [
        {
          ifsc_code: ob.ifsc_code,
          bank_acc_num: ob.bank_acc_num,
          bank_acc_type: ob.bank_acc_type || "SB",
          account_owner: ob.account_owner || "SELF",
        },
      ];
    }
  }

  const newDep = {
    depository_code: (form.depository_code || "CDSL").trim().toUpperCase(),
    dp_id: form.dp_id.trim(),
    client_id: form.client_id.trim(),
    bank_account: form.bank_acc_num.trim(),
    account_owner: "SELF",
    is_default: true,
  };
  const od = form._originalDepository;
  const depChanged =
    !od ||
    String(od.depository_code || "").toUpperCase() !== newDep.depository_code ||
    String(od.dp_id || "").trim() !== newDep.dp_id ||
    String(od.client_id || "").trim() !== newDep.client_id;

  if (depChanged) {
    data.depository = { add: [newDep] };
    if (od?.dp_id && od?.client_id) {
      data.depository.delete = [
        {
          depository_code: od.depository_code || "CDSL",
          dp_id: od.dp_id,
          client_id: od.client_id,
          bank_account: od.bank_account || od.bank_acc_num || form.bank_acc_num.trim(),
          account_owner: od.account_owner || "SELF",
        },
      ];
    }
  }

  return { data };
}
